// src/services/prebuiltAgentApi.ts — Prebuilt Agent Store API endpoints
import { apiClient } from './apiClient';

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface PrebuiltAgent {
  id: number;
  name: string;
  slug: string;
  description: string;
  category: string;
  dockerImage: string;
  requiredEnvVars: EnvVarSpec[];
  capabilities: string[];
  seatFeeHourly: number;
  seatFeeMonthly: number;
  isFeatured: boolean;
  isActive: boolean;
  version: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface EnvVarSpec {
  name: string;
  label: string;
  description: string;
  required: boolean;
  sensitive: boolean;
  type?: 'text' | 'select';
  options?: string[];
  defaultValue?: string;
  placeholder?: string;
}

export interface AgentInstance {
  id: number;
  userId: number;
  catalogId: number;
  serviceId: number;
  name: string;
  status: 'deploying' | 'running' | 'stopped' | 'suspended' | 'failed' | 'unhealthy';
  plan: string;
  billingType: 'hourly' | 'monthly';
  accessUrl: string;
  subdomain?: string;
  firmId?: number;
  firmRole?: string;
  createdAt: Date;
  updatedAt: Date;
  catalogSlug?: string;
  catalogName?: string;
}

export interface AgentConnection {
  id: number;
  agentInstanceId: number;
  targetType: 'service' | 'agent';
  targetId: number;
  targetName: string;
  internalUrl: string;
  permissions: string[];
  isActive: boolean;
  createdAt: Date;
}

export interface AgentFirm {
  id: number;
  userId: number;
  name: string;
  templateSlug: string;
  memoryVolumeName: string;
  status: string;
  members?: AgentInstance[];
  createdAt: Date;
}

export interface SystemContextCard {
  identity: {
    name: string;
    role: string;
    catalog_slug: string;
    deployment_id: number;
    user_id: number;
  };
  capabilities: string[];
  firm: {
    firm_id: number | null;
    role: string | null;
  };
  services: ServiceConnection[];
  agents: AgentPeer[];
  cron_jobs: CronJobConnection[];
  rules: string[];
}

export interface ServiceConnection {
  id: number;
  name: string;
  type: string;
  internal_url: string;
  permissions: string[];
}

export interface AgentPeer {
  id: number;
  name: string;
  catalog_slug: string;
  internal_url: string;
  capabilities: string[];
}

export interface CronJobConnection {
  id: number;
  name: string;
  internal_url: string;
  trigger_only: true;
}

export interface SeatBilling {
  id: number;
  instanceId: number;
  userId: number;
  catalogId: number;
  seatFeePaise: number;
  billingType: 'hourly' | 'monthly';
  status: 'active' | 'stopped' | 'suspended';
  nextBillingAt: Date;
  lastChargedAt?: Date;
  totalChargedInr: number;
  stoppedAt?: Date;
  createdAt: Date;
}

export interface DeployAgentRequest {
  catalogSlug: string;
  name: string;
  plan: string;
  billingType: 'hourly' | 'monthly';
  envVars: Record<string, string>;
  networkId?: number | null;
  connections?: {
    services?: number[];
    agents?: number[];
  };
}

export interface DeployFirmRequest {
  templateSlug: string;
  name: string;
}

export interface BrowseCatalogParams {
  category?: string;
  search?: string;
  sort?: 'name' | 'price' | 'featured';
}

export interface GetMyAgentsParams {
  status?: 'deploying' | 'running' | 'stopped' | 'suspended' | 'failed' | 'unhealthy';
}

export interface PricingInfo {
  infrastructureCost: {
    hourly: number;
    monthly: number;
  };
  seatFee: {
    hourly: number;
    monthly: number;
  };
  total: {
    hourly: number;
    monthly: number;
  };
}

export interface VolumeInfo {
  name: string;
  size: string;
  used: string;
  available: string;
}

// ============================================
// API ENDPOINTS
// ============================================

export const prebuiltAgentApi = {
  // ============================================
  // PUBLIC ENDPOINTS (No Auth)
  // ============================================

  /**
   * Browse the agent catalog with optional filtering
   * GET /v1/prebuilt-agents/catalog
   */
  browseAgentCatalog: (params: BrowseCatalogParams = {}): Promise<{ agents: PrebuiltAgent[]; categories: string[] }> => {
    const query = new URLSearchParams();
    if (params.category) query.append('category', params.category);
    if (params.search) query.append('search', params.search);
    if (params.sort) query.append('sort', params.sort);
    const queryString = query.toString();
    return apiClient._agentGet(`/v1/prebuilt-agents/catalog${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get detailed information about a specific agent
   * GET /v1/prebuilt-agents/catalog/:slug
   */
  getAgentDetail: (slug: string): Promise<{ agent: PrebuiltAgent; pricing: PricingInfo }> =>
    apiClient._agentGet(`/v1/prebuilt-agents/catalog/${slug}`),

  // ============================================
  // PROTECTED ENDPOINTS (Auth Required)
  // ============================================

  /**
   * Deploy a new agent instance
   * POST /v1/prebuilt-agents/deploy
   */
  deployAgent: (data: DeployAgentRequest): Promise<{ instanceId: number; deploymentId: number; status: string }> =>
    apiClient._agentPost('/v1/prebuilt-agents/deploy', data),

  /**
   * Get all agent instances owned by current user
   * GET /v1/prebuilt-agents/instances
   */
  getMyAgents: (params: GetMyAgentsParams = {}): Promise<{ instances: AgentInstance[] }> => {
    const query = params.status ? `?status=${params.status}` : '';
    return apiClient._agentGet(`/v1/prebuilt-agents/instances${query}`);
  },

  /**
   * Get detailed information about a specific agent instance
   * GET /v1/prebuilt-agents/instances/:id
   */
  getAgentInstance: (id: string | number): Promise<{ instance: AgentInstance; connections: AgentConnection[]; billing: SeatBilling }> =>
    apiClient._agentGet(`/v1/prebuilt-agents/instances/${id}`),

  /**
   * Get real-time logs from an agent instance
   * GET /v1/prebuilt-agents/instances/:id/logs
   * Note: This should be upgraded to WebSocket in production
   */
  getAgentLogs: (id: string | number, lines: number = 500): Promise<{ logs: string[] }> =>
    apiClient._agentGet(`/v1/prebuilt-agents/instances/${id}/logs?lines=${lines}`),

  /**
   * Get agent credentials (e.g., OpenClaw token)
   * GET /v1/prebuilt-agents/instances/:id/credentials
   */
  getAgentInstanceCredentials: (id: string | number): Promise<{ success: boolean; agentName: string; agentSlug: string; credentials: any }> =>
    apiClient._agentGet(`/v1/prebuilt-agents/instances/${id}/credentials`),

  /**
   * Connect an agent to a service
   * POST /v1/prebuilt-agents/instances/:id/connect-service
   */
  connectAgentToService: (agentId: string | number, serviceId: string | number): Promise<{ connection: AgentConnection; updatedContext: SystemContextCard }> =>
    apiClient._agentPost(`/v1/prebuilt-agents/instances/${agentId}/connect-service`, { serviceId }),

  /**
   * Connect an agent to another agent
   * POST /v1/prebuilt-agents/instances/:id/connect-agent
   */
  connectAgentToAgent: (agentId: string | number, targetAgentId: string | number): Promise<{ connection: AgentConnection; updatedContext: SystemContextCard }> =>
    apiClient._agentPost(`/v1/prebuilt-agents/instances/${agentId}/connect-agent`, { targetAgentInstanceId: targetAgentId }),

  /**
   * Remove a connection from an agent
   * DELETE /v1/prebuilt-agents/instances/:id/connections/:connId
   */
  deleteConnection: (agentId: string | number, connectionId: number): Promise<{ success: boolean; updatedContext: SystemContextCard }> =>
    apiClient._agentDel(`/v1/prebuilt-agents/instances/${agentId}/connections/${connectionId}`),

  /**
   * Restart an agent instance
   * POST /v1/prebuilt-agents/instances/:id/restart
   */
  restartAgent: (id: string | number): Promise<{ success: boolean; status: string }> =>
    apiClient._agentPost(`/v1/prebuilt-agents/instances/${id}/restart`, {}),

  /**
   * Stop an agent instance (stops billing)
   * POST /v1/prebuilt-agents/instances/:id/stop
   */
  stopAgent: (id: string | number): Promise<{ success: boolean; status: string }> =>
    apiClient._agentPost(`/v1/prebuilt-agents/instances/${id}/stop`, {}),

  /**
   * Start a stopped agent instance (resumes billing)
   * POST /v1/prebuilt-agents/instances/:id/start
   */
  startAgent: (id: string | number): Promise<{ success: boolean; status: string }> =>
    apiClient._agentPost(`/v1/prebuilt-agents/instances/${id}/start`, {}),

  /**
   * Delete an agent instance permanently
   * DELETE /v1/prebuilt-agents/instances/:id
   */
  deleteAgent: (id: string | number): Promise<{ success: boolean; message: string }> =>
    apiClient._agentDel(`/v1/prebuilt-agents/instances/${id}`),

  // ============================================
  // AGENT FIRMS (Multi-Agent Teams)
  // ============================================

  /**
   * Deploy a multi-agent firm
   * POST /v1/prebuilt-agents/firms
   */
  deployFirm: (data: DeployFirmRequest): Promise<{ firmId: number; members: AgentInstance[] }> =>
    apiClient._agentPost('/v1/prebuilt-agents/firms', data),

  /**
   * Get all firms owned by current user
   * GET /v1/prebuilt-agents/firms
   */
  getMyFirms: (): Promise<{ firms: AgentFirm[] }> =>
    apiClient._agentGet('/v1/prebuilt-agents/firms'),

  /**
   * Get detailed information about a specific firm
   * GET /v1/prebuilt-agents/firms/:id
   */
  getFirmDetail: (id: string | number): Promise<{ firm: AgentFirm; members: AgentInstance[]; memoryUsage: VolumeInfo }> =>
    apiClient._agentGet(`/v1/prebuilt-agents/firms/${id}`),

  /**
   * Delete a firm and all its member agents
   * DELETE /v1/prebuilt-agents/firms/:id
   */
  deleteFirm: (id: string | number): Promise<{ success: boolean; deletedMembers: number }> =>
    apiClient._agentDel(`/v1/prebuilt-agents/firms/${id}`)
};
